/**
 * Channel Configuration
 *
 * Thin adapters over `localeConfig` (brand-aware fallback JSON). Prefer
 * `localeConfig` directly for new code — these helpers exist for call sites
 * that take a country code or need reverse channel lookups.
 */

import { localeConfig } from './configuration';

export interface CountryInfo {
  code: string;
  name: string;
  channel: string;
  currency: string;
  supported: boolean;
}

const DEFAULT_CURRENCY = 'USD';

function toCountryInfo(
  code: string,
  channel: string,
  currency: string,
  name: string,
  supported = true
): CountryInfo {
  return { code, name, channel, currency, supported };
}

/**
 * Get currency code for a country
 * @param countryCode - Two-letter country code
 * @returns Currency code (e.g., 'USD', 'EUR', 'GBP')
 */
export function getCurrencyCode(countryCode: string): string {
  return localeConfig.getCountryByCode(countryCode)?.currency ?? DEFAULT_CURRENCY;
}

/**
 * Get country info by code
 * @param countryCode - Two-letter country code
 * @returns Country info or null
 */
export function getCountryInfo(countryCode: string): CountryInfo | null {
  const country = localeConfig.getCountryByCode(countryCode);
  if (!country) return null;

  return toCountryInfo(
    country.code,
    country.channelSlug,
    country.currency,
    country.name,
    country.supported
  );
}

/**
 * All countries known to locale routing (live and catalog-only).
 * SEO live-market filtering uses `localeConfig.getLiveMarkets()` instead.
 */
export function getSupportedCountries(): CountryInfo[] {
  return localeConfig
    .getSupportedCountries()
    .map((country) =>
      toCountryInfo(
        country.code,
        country.channelSlug,
        country.currency,
        country.name,
        country.supported
      )
    );
}

/**
 * Extract country code from locale string
 * @param locale - Locale string like 'us-en', 'gb-en', 'de-de'
 * @returns Country code like 'us', 'gb', 'de'
 */
export function getCountryFromLocale(locale: string): string {
  const parts = locale.split('-');
  return parts[0] || 'us';
}

/**
 * Get channel slug from locale
 *
 * @deprecated Prefer `localeConfig.getChannelSlug(routerLocale)`.
 *
 * @param locale - Locale string like 'us-en', 'gb-en'
 * @returns Saleor channel slug
 */
export function getChannelFromLocale(locale: string): string {
  return localeConfig.getChannelSlug(locale);
}

/**
 * Get currency code from a channel slug
 * @param channelSlug - Saleor channel slug like 'us-usd', 'gb-gbp'
 * @returns Currency code (e.g., 'USD', 'EUR', 'GBP')
 */
export function getCurrencyFromChannel(channelSlug: string): string {
  const slug = channelSlug.toLowerCase();
  const country = localeConfig
    .getSupportedCountries()
    .find((c) => c.channelSlug.toLowerCase() === slug);

  if (country?.currency) {
    return country.currency;
  }

  // Fallback: try to parse from slug (e.g., 'us-usd' -> 'USD')
  const parts = channelSlug.split('-');
  if (parts.length >= 2 && parts[1].length === 3) {
    return parts[1].toUpperCase();
  }

  return DEFAULT_CURRENCY;
}

/**
 * Get the country code associated with a channel slug.
 * Looks up the country whose channel matches the given slug.
 * @param channelSlug - Saleor channel slug like 'us-usd', 'gb-gbp'
 * @returns Two-letter country code (lowercase) or null
 */
export function getCountryFromChannel(channelSlug: string): string | null {
  const slug = channelSlug.toLowerCase();
  const country = localeConfig
    .getSupportedCountries()
    .find((c) => c.channelSlug.toLowerCase() === slug);
  return country ? country.code.toLowerCase() : null;
}
